import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  input,
  OnDestroy,
  output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {FormArray, FormGroup, ValidatorFn} from '@angular/forms';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeApiModule} from 'se-fe-api';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeImageUploaderComponent, SeFeImageUploaderModule} from 'se-fe-image-uploader';
import {SeFeTooltipModule} from 'se-fe-tooltip';
import * as ImageFormUtils from '../../../utils/image-form-utils';
import {
  ImageUploadLimitExceededEvent,
  ImageUploadLimitGuardDirective,
} from '../../directives/image-upload-limit-guard.directive';

/**
 * Multi-image upload field: uploader, limit alerts, image rows, and reordering.
 * Parents own the images `FormArray` and optional surrounding form group.
 */
@Component({
  selector: 'multi-image-upload-field',
  standalone: true,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    ImageUploadLimitGuardDirective,
    SeFeAlertModule,
    SeFeApiModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeIconModule,
    SeFeImageUploaderModule,
    SeFeTooltipModule,
  ],
  templateUrl: './multi-image-upload-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './multi-image-upload-field.component.scss',
})
export class MultiImageUploadFieldComponent implements OnDestroy {
  @ViewChild('imageUploader') imageUploader?: SeFeImageUploaderComponent;

  imagesFormArray = input.required<FormArray<ImageFormUtils.ImageFormGroup>>();
  /** Default alt text applied to newly uploaded images. */
  defaultAlt = input('');
  altValidators = input<ValidatorFn[]>([]);
  parentForm = input<FormGroup | null>(null);
  maxImageUploadCount = input(ImageFormUtils.MAX_IMAGE_UPLOAD_COUNT);
  defaultImgUrl = input('');
  showUploadFailureAlert = input(false);
  clearImagesOnManualUploaderClear = input(false);
  showDescriptionHelp = input(false);
  uploaderMinHeightPx = input<number | null>(null);

  uploadFailed = output<void>();
  uploadSucceeded = output<void>();
  manualUploadCleared = output<void>();
  imagesChanged = output<void>();

  readonly imageUploadLimitErrorMessage = ImageFormUtils.IMAGE_UPLOAD_LIMIT_ERROR_MESSAGE;
  readonly imageUploadFailureMessage = ImageFormUtils.IMAGE_UPLOAD_GENERIC_FAILURE_MESSAGE;
  readonly getImageDescriptionConfig = ImageFormUtils.getImageDescriptionConfig;

  imageUploadLimitError = false;
  imageUploadRejectedCount = 0;
  imageUploadAcceptedCount = 0;
  imageUploadFailureError = false;
  isReorderingImages = false;
  reorderStatusMessage = '';

  private ignoreNextUploadCleared = false;
  private reorderStatusMessageTimeout: ReturnType<typeof setTimeout> | null = null;
  private existingAttachmentsCache: {url: string; filename?: string}[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.clearReorderStatus();
  }

  get existingAttachments(): {url: string; filename?: string}[] {
    const attachments = ImageFormUtils.mapImageFormArrayToImages(this.imagesFormArray()).map(image => ({
      url: image.src,
      ...(image.alt ? {filename: image.alt} : {}),
    }));

    // The uploader re-emits seeded images; keep unchanged inputs stable to avoid a change-detection loop.
    if (
      attachments.length !== this.existingAttachmentsCache.length ||
      attachments.some(
        (attachment, index) =>
          attachment.url !== this.existingAttachmentsCache[index].url ||
          attachment.filename !== this.existingAttachmentsCache[index].filename
      )
    ) {
      this.existingAttachmentsCache = attachments;
    }
    return this.existingAttachmentsCache;
  }

  get imageUploadLimitReached(): boolean {
    return ImageFormUtils.hasReachedImageUploadLimit(this.imagesFormArray(), this.maxImageUploadCount());
  }

  get remainingImageUploadSlots(): number {
    return ImageFormUtils.remainingImageUploadSlots(this.imagesFormArray(), this.maxImageUploadCount());
  }

  get imageUploadLimitSubheaderMessage(): string {
    return this.imageUploadRejectedCount > 0
      ? ImageFormUtils.getImageUploadLimitSubheaderMessage(this.imageUploadRejectedCount, this.imageUploadAcceptedCount)
      : '';
  }

  getSrcControl = ImageFormUtils.getSrcControl;

  getSrcControlValue = ImageFormUtils.getSrcControlValue;

  getAltControl = ImageFormUtils.getAltControl;

  getAltControlValue = ImageFormUtils.getAltControlValue;

  /** Whether the underlying uploader still has files uploading. */
  hasUploadInProgress(): boolean {
    return !!this.imageUploader?.uploadedItems?.some(item => item.isLoading);
  }

  /** Resets the embedded image uploader widget. */
  resetUploader(): void {
    this.imageUploader?.reset?.();
  }

  resetUploadAlerts(): void {
    this.clearImageUploadLimitError();
    this.imageUploadFailureError = false;
    this.ignoreNextUploadCleared = true;
    this.resetUploader();
    queueMicrotask(() => {
      this.ignoreNextUploadCleared = false;
    });
    this.cdr.markForCheck();
  }

  onImageSuccess(uploadedImages: {url: string} | {url: string}[]): void {
    const list = Array.isArray(uploadedImages) ? uploadedImages : [uploadedImages];
    const imageUrls = list.map(item => item?.url ?? '').filter(Boolean);
    const hadRejectedImages = this.imageUploadRejectedCount > 0;

    if (!imageUrls.length) {
      if (this.showUploadFailureAlert()) {
        this.imageUploadFailureError = true;
      }
      this.clearImageUploadLimitError();
      this.syncParentForm();
      return;
    }

    const defaultAlt = this.defaultAlt().trim();
    // eslint-disable-next-line no-useless-assignment
    let uploadAccepted = false;
    const existingSrcs = new Set(
      this.imagesFormArray()
        .controls.map(control => control.get('src')?.value ?? '')
        .filter(Boolean)
    );
    const hasNewUrls = imageUrls.some(url => !existingSrcs.has(url));
    const formLengthBefore = this.imagesFormArray().length;

    if (!this.imageUploadLimitReached) {
      uploadAccepted = ImageFormUtils.appendUniqueUploadedImages(
        this.imagesFormArray(),
        imageUrls,
        defaultAlt,
        this.createImageFormGroup.bind(this),
        false,
        this.maxImageUploadCount()
      );
    } else if (!hasNewUrls) {
      this.cdr.markForCheck();
      return;
    } else {
      const rejectedCount = ImageFormUtils.countRejectedImageUploadUrls(
        this.imagesFormArray(),
        imageUrls,
        this.maxImageUploadCount()
      );
      if (rejectedCount > 0) {
        this.setImageUploadLimitExceeded(rejectedCount, 0);
      }
      this.cdr.markForCheck();
      return;
    }

    if (uploadAccepted) {
      this.resetImageUploaderAfterSuccess();
      if (this.showUploadFailureAlert()) {
        this.imageUploadFailureError = false;
      }
      const rejectedCount = ImageFormUtils.countRejectedImageUploadUrls(
        this.imagesFormArray(),
        imageUrls,
        this.maxImageUploadCount()
      );
      const acceptedCount = this.imagesFormArray().length - formLengthBefore;
      if (rejectedCount > 0) {
        this.setImageUploadLimitExceeded(rejectedCount, acceptedCount);
      } else if (!hadRejectedImages) {
        this.clearImageUploadLimitError();
      }
      this.uploadSucceeded.emit();
      this.syncParentForm();
    }
    this.cdr.markForCheck();
  }

  onImageUploadLimitExceeded(event?: ImageUploadLimitExceededEvent): void {
    const rejectedCount = event
      ? Math.max(0, event.selectedCount - event.remainingSlots)
      : this.imageUploadRejectedCount;
    const acceptedCount = event ? Math.min(event.remainingSlots, event.selectedCount) : this.imageUploadAcceptedCount;

    this.setImageUploadLimitExceeded(rejectedCount, acceptedCount);
    if (this.showUploadFailureAlert()) {
      this.imageUploadFailureError = false;
    }
    this.cdr.markForCheck();
  }

  onImageFailure(): void {
    if (this.showUploadFailureAlert()) {
      this.imageUploadFailureError = true;
    }
    this.clearImageUploadLimitError();
    this.uploadFailed.emit();
    this.cdr.markForCheck();
  }

  removeImage(index: number): void {
    if (!this.isValidImageIndex(index)) {
      return;
    }

    this.imagesFormArray().removeAt(index);
    this.clearImageUploadLimitError();
    if (!this.canReorderImages()) {
      this.isReorderingImages = false;
      this.clearReorderStatus();
    }
    this.syncParentForm();
  }

  canReorderImages(): boolean {
    return this.imagesFormArray().length > 1;
  }

  startImageReorder(): void {
    if (!this.canReorderImages()) {
      return;
    }
    this.isReorderingImages = true;
    this.clearReorderStatus();
    this.cdr.markForCheck();
  }

  finishImageReorder(): void {
    this.isReorderingImages = false;
    this.clearReorderStatus();
    this.cdr.markForCheck();
  }

  dropImage(event: CdkDragDrop<ImageFormUtils.ImageFormGroup[]>): void {
    this.moveImage(event.previousIndex, event.currentIndex);
  }

  moveImage(previousIndex: number, currentIndex: number): boolean {
    if (!this.isReorderingImages || !this.canReorderImages() || previousIndex === currentIndex) {
      return false;
    }

    if (!this.isValidImageIndex(previousIndex) || !this.isValidImageIndex(currentIndex)) {
      return false;
    }

    const imageControl = this.imagesFormArray().at(previousIndex);
    this.imagesFormArray().removeAt(previousIndex);
    this.imagesFormArray().insert(currentIndex, imageControl);
    this.syncParentForm();
    return true;
  }

  handleReorderKeydown(event: KeyboardEvent, index: number): void {
    if (!this.isReorderingImages) {
      return;
    }

    const keyToIndex: Record<string, number> = {
      ArrowUp: index - 1,
      ArrowDown: index + 1,
      Home: 0,
      End: this.imagesFormArray().length - 1,
    };
    const nextIndex = keyToIndex[event.key];

    if (nextIndex === undefined) {
      return;
    }

    event.preventDefault();
    if (this.moveImage(index, nextIndex)) {
      this.announceImageReorder(nextIndex);
    }
  }

  imageUploadCleared(): void {
    if (this.ignoreNextUploadCleared) {
      this.ignoreNextUploadCleared = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.imageUploadLimitError) {
      this.cdr.markForCheck();
      return;
    }

    if (this.clearImagesOnManualUploaderClear()) {
      this.imagesFormArray().clear();
      this.clearImageUploadLimitError();
      if (this.showUploadFailureAlert()) {
        this.imageUploadFailureError = false;
      }
      this.manualUploadCleared.emit();
      this.syncParentForm();
      return;
    }

    this.cdr.markForCheck();
  }

  private createImageFormGroup(src = '', alt = ''): ImageFormUtils.ImageFormGroup {
    return ImageFormUtils.createImageFormGroup(src, alt, this.altValidators());
  }

  private resetImageUploaderAfterSuccess(): void {
    if (!this.imageUploader?.reset) {
      return;
    }

    this.ignoreNextUploadCleared = true;
    this.imageUploader.reset();
    queueMicrotask(() => {
      this.ignoreNextUploadCleared = false;
    });
  }

  private syncParentForm(): void {
    this.imagesFormArray().updateValueAndValidity({emitEvent: false});
    this.parentForm()?.updateValueAndValidity({emitEvent: false});
    this.imagesChanged.emit();
    this.cdr.markForCheck();
  }

  private isValidImageIndex(index: number): boolean {
    return index >= 0 && index < this.imagesFormArray().length;
  }

  private announceImageReorder(currentIndex: number): void {
    const nextMessage = `Moved image to position ${currentIndex + 1} of ${this.imagesFormArray().length}.`;
    this.clearReorderStatus();
    this.reorderStatusMessageTimeout = setTimeout(() => {
      this.reorderStatusMessage = nextMessage;
      this.reorderStatusMessageTimeout = null;
      this.cdr.markForCheck();
    }, 0);
  }

  private clearReorderStatus(): void {
    if (this.reorderStatusMessageTimeout !== null) {
      clearTimeout(this.reorderStatusMessageTimeout);
      this.reorderStatusMessageTimeout = null;
    }
    this.reorderStatusMessage = '';
  }

  private setImageUploadLimitExceeded(rejectedCount: number, acceptedCount: number): void {
    this.imageUploadLimitError = true;
    this.imageUploadRejectedCount = Math.max(0, rejectedCount);
    this.imageUploadAcceptedCount = Math.max(0, acceptedCount);
  }

  private clearImageUploadLimitError(): void {
    this.imageUploadLimitError = false;
    this.imageUploadRejectedCount = 0;
    this.imageUploadAcceptedCount = 0;
  }
}
